import { Bell, User, LogOut, UserCircle, Settings, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useState, useRef, useEffect } from 'react';
import { useAppStore } from '../../stores/appStore';
import { Badge } from '../ui/Badge';
import amplinthLogo from '../../assets/images/amplinthLogo-new.jpeg';

export default function Header() {
  const navigate = useNavigate();
  const currentUser = useAppStore((state) => state.currentUser);
  const logout = useAppStore((state) => state.logout);
  const notifications = useAppStore((state) => state.notifications);
  const unreadCount = notifications.filter((n) => !n.read).length;

  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Check if user is admin
  const isAdmin = currentUser?.role === 'Admin';

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleMenuItemClick = (path: string) => {
    setShowUserMenu(false);
    navigate(path);
  };

  return (
    <header className="sticky top-0 z-50 h-16 bg-white border-b border-neutral-200 shadow-sm">
      <div className="h-full px-6 flex items-center justify-between">
        {/* Logo and Title - Clickable to go to Dashboard */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
          aria-label="Go to Dashboard"
        >
          <img
            src={amplinthLogo}
            alt="Amplinth Logo"
            className="h-16 w-auto"
          />
          <div className="border-l border-neutral-300 pl-3">
            <p className="text-neutral-600 font-medium" style={{ fontSize: '15.6px' }}>Business Transformation Studio</p>
          </div>
        </button>

        {/* Right side: Notifications and User */}
        <div className="flex items-center gap-4">
          {/* Notifications */}
          <button
            className="relative p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5 text-neutral-600" />
            {unreadCount > 0 && (
              <Badge
                variant="error"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] px-1"
              >
                {unreadCount}
              </Badge>
            )}
          </button>

          {/* User Menu */}
          <div className="flex items-center gap-3 pl-3 border-l border-neutral-200 relative" ref={menuRef}>
            <div className="text-right">
              <p className="text-sm font-medium text-neutral-900">
                {currentUser?.name || 'Guest User'}
              </p>
              <p className="text-xs text-neutral-500">
                {currentUser?.role || 'Consultant'}
              </p>
            </div>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="w-9 h-9 bg-secondary-100 rounded-full flex items-center justify-center hover:bg-secondary-200 transition-colors"
              aria-label="User menu"
            >
              <User className="w-5 h-5 text-secondary-700" />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-50">
                <button
                  onClick={() => handleMenuItemClick('/profile')}
                  className="w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50 flex items-center gap-3 transition-colors"
                >
                  <UserCircle className="w-4 h-4" />
                  Profile
                </button>
                <button
                  onClick={() => handleMenuItemClick('/settings')}
                  className="w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50 flex items-center gap-3 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </button>
                {isAdmin && (
                  <button
                    onClick={() => handleMenuItemClick('/admin')}
                    className="w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50 flex items-center gap-3 transition-colors"
                  >
                    <Shield className="w-4 h-4" />
                    Admin
                  </button>
                )}
                <div className="border-t border-neutral-200 my-1"></div>
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
